import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, MenuSelect } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import type { StationChainStopValue } from '../stationChainFormSchema';

export interface StopRowProps {
  stop: StationChainStopValue;
  index: number;
  color: string;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent) => void;
  onDrop: () => void;
  onRemove: () => void;
  onDwell: (raw: string) => void;
  onSetDetail: (stationDetailId: number | null) => void;
}

function coordLabel(lat: number | null, lng: number | null): string | undefined {
  return lat != null && lng != null ? `${lat.toFixed(3)}, ${lng.toFixed(3)}` : undefined;
}

/** Řádek zastávky — pořadové číslo v barvě trasy, identita stanice, stání, nástupní místo, odebrání. */
export function StopRow({
  stop,
  index,
  color,
  dragging,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onRemove,
  onDwell,
  onSetDetail,
}: StopRowProps) {
  const { t } = useTranslation('products');
  const meta = [stop.stationCode, coordLabel(stop.stationLat, stop.stationLng)]
    .filter(Boolean)
    .join('  ');

  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`station-chain-stop-${index}`}
      className={cn(
        'border-phx-border-2 rounded-phx bg-phx-surface border',
        dragging && 'opacity-50'
      )}
    >
      {/* Grip i pořadí vertikálně vycentrované vůči celému řádku (vč. výběru nástupního místa)
          díky items-center + obsahovému sloupci, který drží název/kód i select. */}
      <div className="px-phx-3 py-phx-2 gap-phx-3 flex items-center">
        <span
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          className="text-phx-text-faint hover:text-phx-text-muted cursor-grab leading-none select-none"
          title={t('stationChain.stops.drag')}
          aria-label={t('stationChain.stops.drag')}
          data-testid={`station-chain-drag-${index}`}
        >
          ⠿
        </span>
        <span
          className="text-phx-xs inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-semibold text-white"
          style={{ backgroundColor: color }}
        >
          {index + 1}
        </span>

        <div className="gap-phx-2 flex min-w-0 flex-1 flex-col">
          <div className="min-w-0">
            <div className="text-phx-sm text-phx-text truncate font-semibold">
              {stop.stationName}
            </div>
            {meta ? (
              <div className="text-phx-xs text-phx-text-muted truncate font-mono">{meta}</div>
            ) : null}
          </div>

          {/* Nástupní místo — pod název/kód/GPS; bez labelu, jen placeholder + ikona. */}
          {stop.points.length > 0 ? (
            <MenuSelect
              size="sm"
              icon="pin"
              className="max-w-60"
              value={stop.stationDetailId != null ? String(stop.stationDetailId) : null}
              placeholder={t('stationChain.stops.boardingPoint')}
              emptyOptionLabel={t('stationChain.stops.boardingPointNone')}
              menuLabel={t('stationChain.stops.boardingPoint')}
              ariaLabel={t('stationChain.stops.boardingPoint')}
              options={stop.points.map((point) => ({
                value: String(point.id),
                label: point.name,
                hint: coordLabel(point.lat, point.lng),
              }))}
              onChange={(next) => onSetDetail(next ? Number(next) : null)}
            />
          ) : null}
        </div>

        <div className="gap-phx-1 flex flex-col items-end">
          <span className="text-phx-text-muted text-phx-2xs font-medium tracking-wide uppercase">
            {t('stationChain.stops.dwellShort')}
          </span>
          <Input
            type="number"
            size="xs"
            min={0}
            value={stop.dwell}
            unit={t('stationChain.stops.min')}
            onChange={(event) => onDwell(event.target.value)}
            className="w-20"
            data-testid={`station-chain-dwell-${index}`}
          />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          iconLeft="x"
          aria-label={t('stationChain.stops.remove')}
          onClick={onRemove}
          data-testid={`station-chain-remove-${index}`}
        />
      </div>
    </div>
  );
}
